import { Link, useNavigate } from '@tanstack/react-router'
import { Check, Edit, LayoutTemplate, QrCode, Share2, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useEvent, usePublishEvent } from '~/entities/event/model/use-events'
import { FlyerGeneratorDialog, QrGeneratorDialog } from '~/features/ai-studio/components'
import { DeleteEventDialog } from '~/features/events/components/delete-event-dialog'
import { EventRequestsButton } from '~/features/events/components/event-requests-button'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import { richTextToPlainText } from '~/shared/lib/rich-text'
import { copyToClipboard, getEventShareUrl } from '~/shared/lib/share'
import { DESTRUCTIVE_OUTLINE_COLORS } from '~/shared/styles/buttons'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~/shared/ui/alert-dialog'
import { Button } from '~/shared/ui/button'
import { Skeleton } from '~/shared/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shared/ui/tooltip'

export function EventDetailActions({ eventId }: { eventId: string }) {
  const navigate = useNavigate()
  const { data: business } = useCurrentBusiness()
  const { data: event, isLoading } = useEvent(business?.id ?? '', eventId)
  const [deleting, setDeleting] = useState(false)
  const publishEvent = usePublishEvent(business?.id ?? '')

  const handlePublish = async () => {
    if (!event) return
    try {
      await publishEvent.mutateAsync(eventId)
      toast.success(m.eventPublished(), {
        description: m.eventIsNowLiveAndVisibleToUsers({ eventTitle: event.title }),
      })
    } catch (error) {
      toast.error(m.failedToPublishEvent(), { description: getErrorMessageFromError(error) })
    }
  }

  const handleShare = async () => {
    const ok = await copyToClipboard(getEventShareUrl(eventId))
    if (ok) {
      toast.success(m.linkCopied(), { description: m.linkCopiedToClipboard() })
    } else {
      toast.error(m.failedToCopyLink())
    }
  }

  if (isLoading || !business) {
    return <Skeleton className="h-9 w-full max-w-lg rounded-lg" />
  }

  if (!event) {
    return null
  }

  const isDraft = event.isDraft || event.status === 'Draft'
  // Matches how the events list buckets its tabs (the backend splits on
  // endTime): a finished draft has nothing left to publish.
  const isPast = new Date(event.endTime) < new Date()

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isDraft &&
        !isPast &&
        (event.coverImageUrl ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={publishEvent.isPending}>
                <Check className="mr-2 h-4 w-4" />
                {m.publish()}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{m.publishEvent()}</AlertDialogTitle>
                <AlertDialogDescription>
                  {m.thisWillMakeTheEventVisibleToAllUsers()}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
                <AlertDialogAction onClick={handlePublish}>{m.publish()}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to="/dashboard/events/$eventId/edit"
                  params={{ eventId }}
                  search={{ scrollTo: 'images' }}
                  className="inline-flex"
                >
                  <Button disabled>
                    <Check className="mr-2 h-4 w-4" />
                    {m.publish()}
                  </Button>
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>{m.addACoverImageToPublish1()}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}

      <Button asChild variant="outline">
        <Link to="/dashboard/events/$eventId/edit" params={{ eventId }}>
          <Edit className="mr-2 h-4 w-4" />
          {m.edit()}
        </Link>
      </Button>

      {event.roomVisibility === 'request' && (
        <EventRequestsButton businessId={business.id} eventId={eventId} />
      )}

      {isDraft ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="outline" disabled>
                  <Share2 className="mr-2 h-4 w-4" />
                  {m.shareLink()}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{m.publishBeforeSharing()}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Button variant="outline" onClick={handleShare}>
          <Share2 className="mr-2 h-4 w-4" />
          {m.shareLink()}
        </Button>
      )}

      {isDraft ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="outline" disabled>
                  <QrCode className="mr-2 h-4 w-4" />
                  {m.qrCode()}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{m.publishBeforeSharing()}</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button variant="outline" disabled>
                  <LayoutTemplate className="mr-2 h-4 w-4" />
                  {m.flyerTool()}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{m.publishBeforeSharing()}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <>
          <QrGeneratorDialog
            data={getEventShareUrl(eventId)}
            label={event.title}
            triggerClassName=""
          />
          <FlyerGeneratorDialog
            qrData={getEventShareUrl(eventId)}
            label={event.title}
            coverUrl={event.coverImageUrl}
            images={event.images}
            description={event.description && richTextToPlainText(event.description)}
            triggerClassName=""
          />
        </>
      )}

      <Button
        variant="outline"
        className={`sm:ml-auto ${DESTRUCTIVE_OUTLINE_COLORS}`}
        onClick={() => setDeleting(true)}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        {m.delete()}
      </Button>
      <DeleteEventDialog
        businessId={business.id}
        eventId={eventId}
        eventTitle={event.title}
        open={deleting}
        onOpenChange={setDeleting}
        onDeleted={() => navigate({ to: '/dashboard/events' })}
      />
    </div>
  )
}
